import {
  Box,
  Button,
  Container,
  createToaster,
  Flex,
  Grid,
  Heading,
  Icon,
  Text,
  VStack
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { FiArrowLeft } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { mockApi } from "../api/mockApi";
import {
  GithubIntegration,
  PathAdvancedSettings,
  PathConfiguration,
  PathSummaryPanel
} from "../components/CreatePath";

// Create toaster instance
const toast = createToaster({
  placement: "top",
  pauseOnPageIdle: true
});

function CreatePath() {
  const navigate = useNavigate();
  const pathInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState({
    path: "",
    githubRepo: "",
    githubSubfolder: "",
    githubTag: "",
    description: "",
    metadata: "",
    forceExternalBrowser: false
  });

  const [metadataError, setMetadataError] = useState<string | null>(null);

  const [repoDropdown, setRepoDropdown] = useState({
    isOpen: false,
    searchQuery: ""
  });

  const [subfolderDropdown, setSubfolderDropdown] = useState({
    isOpen: false,
    searchQuery: ""
  });

  const [tagDropdown, setTagDropdown] = useState({
    isOpen: false,
    searchQuery: ""
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isValidatingPath, setIsValidatingPath] = useState(false);
  const [isValidatingRepo, setIsValidatingRepo] = useState(false);
  const [pathAvailability, setPathAvailability] = useState<{
    checked: boolean;
    available: boolean;
    message?: string;
  }>({ checked: false, available: false });

  // Mock list of available repositories
  const availableRepos = [
    "example/marketing-site",
    "example/webinar-platform",
    "example/documentation",
    "example/mobile-app",
    "example/partner-portal",
    "example/events-site",
    "example/support-center",
    "example/blog",
    "example/default-repo",
    "acme/frontend",
    "acme/backend",
    "myorg/website",
    "myorg/docs"
  ];

  // Query for subfolders
  const { data: subfolders = [], isLoading: isLoadingSubfolders } = useQuery({
    queryKey: ["github-subfolders", formData.githubRepo],
    queryFn: () => mockApi.getGithubRepoSubfolders(formData.githubRepo),
    enabled: !!formData.githubRepo
  });

  // Query for tags
  const { data: tags = [], isLoading: isLoadingTags } = useQuery({
    queryKey: ["github-tags", formData.githubRepo],
    queryFn: () => mockApi.getGithubRepoTags(formData.githubRepo),
    enabled: !!formData.githubRepo
  });

  // Debounced path validation
  useEffect(() => {
    if (!formData.path) {
      setPathAvailability({ checked: false, available: false });
      return;
    }

    const timer = setTimeout(async () => {
      setIsValidatingPath(true);
      try {
        // First validate the path format
        const validationResult = await mockApi.validatePath(formData.path);
        if (!validationResult.valid) {
          setValidationErrors(prev => ({
            ...prev,
            path: validationResult.message || "Invalid path"
          }));
          setPathAvailability({ checked: true, available: false });
        } else {
          // Then check availability
          const availabilityResult = await mockApi.checkPathAvailability(formData.path);
          setPathAvailability({
            checked: true,
            available: availabilityResult.available,
            message: availabilityResult.message
          });
          
          if (!availabilityResult.available) {
            setValidationErrors(prev => ({
              ...prev,
              path: availabilityResult.message || "Path not available"
            }));
          } else {
            setValidationErrors(prev => {
              const { path, ...rest } = prev;
              return rest;
            });
          }
        }
      } catch (error) {
        console.error("Error validating path:", error);
      } finally {
        setIsValidatingPath(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.path]);

  // Validate repository when it changes
  useEffect(() => {
    if (formData.githubRepo) {
      validateRepository();
    }
  }, [formData.githubRepo]);

  const validateRepository = async () => {
    if (!formData.githubRepo) return;
    
    setIsValidatingRepo(true);
    try {
      const result = await mockApi.validateGithubRepo(formData.githubRepo);
      if (!result.valid) {
        setValidationErrors(prev => ({
          ...prev,
          githubRepo: result.message || "Invalid repository"
        }));
      } else {
        setValidationErrors(prev => {
          const { githubRepo, ...rest } = prev;
          return rest;
        });
      }
    } catch (error) {
      console.error("Error validating repository:", error);
    } finally {
      setIsValidatingRepo(false);
    }
  };

  // Handle metadata change
  const handleMetadataChange = (value: string) => {
    setFormData(prev => ({ ...prev, metadata: value }));
    
    let error = null;
    if (value) {
      try {
        JSON.parse(value);
      } catch {
        error = "Invalid JSON format";
      }
    }
    setMetadataError(error);
  };

  // Create path mutation
  const createPathMutation = useMutation({
    mutationFn: () => {
      const dataToSubmit = {
        ...formData,
        metadata: formData.metadata ? JSON.parse(formData.metadata) : undefined
      };
      return mockApi.createPath(dataToSubmit);
    },
    onSuccess: () => {
      toast.create({
        title: "Path Created Successfully",
        description: `Path "${formData.path}" has been created and linked to ${formData.githubRepo}`,
        type: "success",
        duration: 5000
      });
      navigate("/website");
    },
    onError: (error: any) => {
      toast.create({
        title: "Failed to create path",
        description: error.message || "An error occurred while creating the path",
        type: "error",
        duration: 5000
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const errors: Record<string, string> = {};
    
    if (!formData.path) {
      errors.path = "Path is required";
    } else if (!pathAvailability.available) {
      errors.path = pathAvailability.message || "Path is not available";
    }
    
    if (!formData.githubRepo) {
      errors.githubRepo = "GitHub repository is required";
    }
    
    if (!formData.githubTag) {
      errors.githubTag = "Tag or branch is required";
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Focus on first error field
      if (errors.path && pathInputRef.current) {
        pathInputRef.current.focus();
      }
      
      toast.create({
        title: "Validation Error",
        description: "Please fix the errors before submitting",
        type: "error",
        duration: 3000
      });
      return;
    }
    
    createPathMutation.mutate();
  };

  const handleCancel = () => {
    navigate("/website");
  };

  return (
    <Box bg="gray.50" minH="100vh">
      <Container maxW="7xl" py={8}>
        <VStack gap={8} align="stretch">
          {/* Header */}
          <Box>
            <Link to="/website">
              <Button
                variant="ghost"
                size="sm"
                mb={4}
                _hover={{ bg: "gray.100" }}
              >
                <Icon as={FiArrowLeft} mr={2} />
                Back to Website
              </Button>
            </Link>
            <Heading size="xl" color="gray.800">
              Create New Path
            </Heading>
            <Text color="gray.600" mt={2}>
              Configure a new path with GitHub repository integration
            </Text>
          </Box>

          <Grid templateColumns={{ base: "1fr", lg: "1fr 400px" }} gap={8}>
            {/* Main Form */}
            <VStack
              as="form"
              onSubmit={handleSubmit}
              gap={6}
              align="stretch"
              overflow="visible"
              position="relative"
            >
              {/* Path Configuration Section */}
              <PathConfiguration
                path={formData.path}
                description={formData.description}
                isValidatingPath={isValidatingPath}
                pathAvailability={pathAvailability}
                validationErrors={validationErrors}
                onPathChange={(path) => setFormData(prev => ({ ...prev, path }))}
                onDescriptionChange={(description) => setFormData(prev => ({ ...prev, description }))}
                pathInputRef={pathInputRef}
              />

              {/* GitHub Integration Section */}
              <GithubIntegration
                githubRepo={formData.githubRepo}
                githubSubfolder={formData.githubSubfolder}
                githubTag={formData.githubTag}
                repoDropdown={repoDropdown}
                subfolderDropdown={subfolderDropdown}
                tagDropdown={tagDropdown}
                validationErrors={validationErrors}
                isValidatingRepo={isValidatingRepo}
                isLoadingSubfolders={isLoadingSubfolders}
                isLoadingTags={isLoadingTags}
                availableRepos={availableRepos}
                subfolders={subfolders}
                tags={tags}
                onRepoChange={(repo) => setFormData(prev => ({ ...prev, githubRepo: repo, githubSubfolder: "", githubTag: "" }))}
                onSubfolderChange={(subfolder) => setFormData(prev => ({ ...prev, githubSubfolder: subfolder }))}
                onTagChange={(tag) => {
                  setFormData(prev => ({ ...prev, githubTag: tag }));
                  if (validationErrors.githubTag) {
                    setValidationErrors(prev => {
                      const { githubTag, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                onRepoDropdownToggle={(isOpen) => setRepoDropdown(prev => ({ ...prev, isOpen }))}
                onRepoSearchChange={(query) => setRepoDropdown(prev => ({ ...prev, searchQuery: query }))}
                onSubfolderDropdownToggle={(isOpen) => setSubfolderDropdown(prev => ({ ...prev, isOpen }))}
                onSubfolderSearchChange={(query) => setSubfolderDropdown(prev => ({ ...prev, searchQuery: query }))}
                onTagDropdownToggle={(isOpen) => setTagDropdown(prev => ({ ...prev, isOpen }))}
                onTagSearchChange={(query) => setTagDropdown(prev => ({ ...prev, searchQuery: query }))}
              />

              {/* Advanced Settings Section */}
              <PathAdvancedSettings
                metadata={formData.metadata}
                metadataError={metadataError}
                forceExternalBrowser={formData.forceExternalBrowser}
                onMetadataChange={handleMetadataChange}
                onForceExternalBrowserChange={(value) => setFormData(prev => ({ ...prev, forceExternalBrowser: value }))}
              />

              {/* Action Buttons */}
              <Flex justify="flex-end" gap={3}>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  size="lg"
                  px={8}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="purple"
                  size="lg"
                  px={8}
                  loading={createPathMutation.isPending}
                  disabled={!pathAvailability.available && !!formData.path}
                >
                  Create Path
                </Button>
              </Flex>
            </VStack>

            {/* Right Side - Summary Panel */}
            <PathSummaryPanel
              path={formData.path}
              githubRepo={formData.githubRepo}
              githubSubfolder={formData.githubSubfolder}
              githubTag={formData.githubTag}
              metadata={formData.metadata}
              forceExternalBrowser={formData.forceExternalBrowser}
              pathAvailability={pathAvailability}
            />
          </Grid>
        </VStack>
      </Container>
    </Box>
  );
}

export default CreatePath;